import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { listDrafts } from '../api/contentWizardApi';
import MainLayout from '../components/Layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { PageSizeSelector } from '../components/PageSizeSelector';
import { Plus, BookOpen, Edit2, Trash2, Clock, Users as UsersIcon, Eye, Upload, BarChart3, TrendingUp, CheckCircle, AlertTriangle, Loader2, FileText, Download, Search, SlidersHorizontal, ChevronDown, ChevronUp, X, Camera, Globe, Sparkles } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import CourseAnalyticsSummary from '../components/CourseAnalyticsSummary';
import TaxonomyFilterSidebar from '../components/TaxonomyFilterSidebar';
import ImportPackageDialog from '../components/course-package/ImportPackageDialog';

// COURSE PACKAGE IMPORT — ROLLOUT SWITCH.
// Until the new zip importer works end to end, ONLY SuperAdmin gets the new
// "Import a course package" dialog; every other role keeps the legacy JSON
// import (`handleImportCourse`) exactly as before. The FINAL build step flips
// this to `true`, after which the legacy flow is disconnected as planned.
const NEW_PACKAGE_IMPORT_FOR_EVERYONE = true;
import {
  validateBasics,
  validateAudience,
  validateStructure,
  validatePedagogyGenerate,
} from '../components/course-wizard/stepValidation';

// 2026-02 — Compute the "Continue at {step}" resume label for draft
// courses. Mirrors the auto-jump logic in `CourseDefinitionPanel.jsx` —
// walks the same per-step validators and returns the first step name
// that isn't complete. If every step validates, returns "Ready to
// generate" so the user knows they can just click Generate.
//
// The wizard's `formData` shape and the persisted `course` shape have
// slightly different field names — we adapt here rather than in the
// validators (which stay pure).
const _WIZARD_STEP_LABELS = ['Course Basics', 'Target Audience', 'Structure', 'Generate'];

const _courseToFormData = (course) => {
  const profile = course.targetAudienceProfile || {};
  return {
    // Step 1 — Basics
    title: course.title || '',
    description: course.description || '',
    learningObjectives: Array.isArray(course.learningObjectives)
      ? course.learningObjectives.join('\n')
      : course.learningObjectives || '',
    // Step 2 — Target Audience
    organizationType: profile.organizationType || '',
    competenceLevel: profile.competenceLevel || '',
    knowledgeLevel: profile.knowledgeLevel || '',
    englishCompetence: profile.englishCompetence || '',
    // Step 3 — Structure
    deliveryMode: course.deliveryMode || '',
    totalDuration: course.duration || '',
    engagementHoursPerWeek: course.engagementHoursPerWeek || '',
    teachingHoursPerWeek: course.teachingHoursPerWeek || '',
    difficulty: course.difficulty || '',
    aiDomains: course.domains || (course.domain ? [course.domain] : []),
    industryDomains: course.industryDomains || [],
    functionalDomains: course.functionalDomains || [],
    // Step 4 — Pedagogy & Generate
    instructorPersona: course.instructorPersona || '',
    pedagogy: course.pedagogy || null,
  };
};

export const computeDraftResumeLabel = (course) => {
  if (!course || course.lifecycleStage !== 'definition') return null;
  const fd = _courseToFormData(course);
  const hasAnyContent = !!(fd.title || fd.learningObjectives || fd.description);
  if (!hasAnyContent) return null;  // brand-new empty draft → no badge
  if (validateBasics(fd).length > 0)             return { step: 0, label: 'Continue Course Basics' };
  if (validateAudience(fd).length > 0)           return { step: 1, label: 'Continue Target Audience' };
  if (validateStructure(fd).length > 0)          return { step: 2, label: 'Continue Structure' };
  if (validatePedagogyGenerate(fd).length > 0)   return { step: 3, label: 'Continue Generate' };
  return { step: 3, label: 'Ready to generate' };
};
import ImageCard from '../components/ImageCard';
import { formatEditedLong } from '../utils/formatEditedDate';
import ThumbnailEditor from '../components/ThumbnailEditor';
import ImportMarketplaceCourseDialog from '../components/marketplace/ImportMarketplaceCourseDialog';

const CoursesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // PRD v2.2 — Persist the last-selected lifecycle tab per-user so the
  // grid doesn't reset to "All" every time the user comes back from
  // Users/Cohorts. We seed from localStorage if the URL doesn't already
  // carry a `lifecycle` param.
  const lifecycleStorageKey = user?.id ? `courses_grid_lifecycle:${user.id}` : null;
  const initialLifecycleFromStorage = (() => {
    if (typeof window === 'undefined' || !lifecycleStorageKey) return null;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('lifecycle')) return null; // URL wins
      return localStorage.getItem(lifecycleStorageKey) || null;
    } catch (_e) {
      return null;
    }
  })();
  
  // URL-based filter persistence
  const { filters, debouncedFilters, setFilter, clearFilters: resetUrlFilters } = useUrlFilters(
    { search: '', difficulty: 'all', domain: 'all', published: 'all', lifecycle: initialLifecycleFromStorage || 'all', sort: 'updated_desc', page: 1, perPage: 24 },
    { debounce: { search: 300 } }
  );
  const searchQuery = filters.search;
  const setSearchQuery = (v) => setFilter('search', v);
  const debouncedSearch = debouncedFilters.search;
  const sortBy = filters.sort || 'updated_desc';
  const setSortBy = (v) => { setFilter('sort', v); setFilter('page', 1); };
  const difficultyFilter = filters.difficulty;
  const setDifficultyFilter = (v) => setFilter('difficulty', v);
  const domainFilter = filters.domain;
  const setDomainFilter = (v) => setFilter('domain', v);
  const publishedFilter = filters.published;
  const setPublishedFilter = (v) => setFilter('published', v);
  // PRD: Course Merger -- lifecycle tab filter on Courses page.
  const lifecycleFilter = filters.lifecycle || 'all';
  const setLifecycleFilter = (v) => {
    setFilter('lifecycle', v);
    setFilter('page', 1);
  };

  // PRD v2.2 — persist the last-selected lifecycle tab to localStorage
  // whenever it changes (either via tab-click OR via URL navigation), so
  // a fresh visit to /courses without query params restores the user's
  // last view instead of snapping back to "All".
  useEffect(() => {
    if (!lifecycleStorageKey || !lifecycleFilter) return;
    try {
      localStorage.setItem(lifecycleStorageKey, lifecycleFilter);
    } catch (_e) { /* ignore quota errors */ }
  }, [lifecycleFilter, lifecycleStorageKey]);
  const currentPage = debouncedFilters.page;
  const setCurrentPage = (v) => setFilter('page', v);
  const coursesPerPage = Number(filters.perPage) || 24;
  const setCoursesPerPage = (v) => { setFilter('perPage', v); setFilter('page', 1); };
  
  // Courses state with server-side pagination
  const [courses, setCourses] = useState([]);
  const [totalCourses, setTotalCourses] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // PRD v2.2 / S2.6 — when true, the Courses grid also surfaces cohort
  // forks (otherwise they're filtered server-side via `kind=template`).
  const [showForks, setShowForks] = useState(false);
  
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    difficulty: 'Beginner',
    domain: '',
    duration: ''
  });
  
  // Analytics dialog state
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [courseAnalytics, setCourseAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Content Pack PDF download state
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [creators, setCreators] = useState({});
  const [studioCourseIds, setStudioCourseIds] = useState(() => new Set());
  const [fetchingCourses, setFetchingCourses] = useState(false);
  const lastFetchedOrgRef = React.useRef(null);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  
  // Taxonomy filter state
  const [taxonomyTagIds, setTaxonomyTagIds] = useState([]);

  // PRD Cohort-First v2.1 / S7 — Marketplace mode.
  const [marketplaceCourses, setMarketplaceCourses] = useState([]);
  const [loadingMarketplace, setLoadingMarketplace] = useState(false);
  // PRD Cohort-First v2.1 / S7 polish — differentiated empty-state metadata.
  const [marketplaceConsumerOf, setMarketplaceConsumerOf] = useState(null); // null = unfetched, [] = none, [...] = list

  // PRD v2.1 / Slice S8 — Import-from-marketplace flow.
  const [importTarget, setImportTarget] = useState(null); // { course, sourceOrg } | null

  // PRD v2.1 / Slice S8 — cache resolved source-org names for imported courses
  // so the card badges and right panel can show "From <orgName>" without
  // hammering the lookup endpoint per render.
  const [sourceOrgNames, setSourceOrgNames] = useState({}); // { orgId: { name, logoUrl } }

  // Course Package Import (zip) — opens from the existing "Import Course" button.
  const [showImportDialog, setShowImportDialog] = useState(false);
  const orgName = (() => {
    try { return JSON.parse(localStorage.getItem('organization') || '{}')?.name || ''; }
    catch (e) { return ''; }
  })();
  
  // Check if any advanced filters are active
  const hasActiveAdvancedFilters = difficultyFilter !== 'all' || 
    domainFilter !== 'all' || 
    publishedFilter !== 'all' || 
    selectedInstructors.length > 0 || 
    dateRange.start || 
    dateRange.end ||
    taxonomyTagIds.length > 0;
  
  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    resetUrlFilters();
    setSelectedInstructors([]);
    setDateRange({ start: '', end: '' });
    setTaxonomyTagIds([]);
  };
  
  // Fetch courses when filters or page change
  useEffect(() => {
    if (user?.orgId) {
      fetchCourses();
    }
  }, [user?.orgId, currentPage, debouncedSearch, difficultyFilter, domainFilter, publishedFilter, lifecycleFilter, sortBy, coursesPerPage, taxonomyTagIds, showForks]);

  // PRD Cohort-First v2.1 / S7 — when the Marketplace pill is active, fetch the
  // alternate "courses available to me from any marketplace" payload instead.
  useEffect(() => {
    let cancelled = false;
    if (lifecycleFilter !== 'marketplace' || !user?.orgId) {
      setMarketplaceCourses([]);
      setMarketplaceConsumerOf(null);
      return undefined;
    }
    (async () => {
      setLoadingMarketplace(true);
      try {
        // Parallelize the two calls so the empty-state diagnosis is ready by
        // the time we know the grid is empty.
        const [availResp, consumerOfResp] = await Promise.all([
          api.get(`/organizations/${user.orgId}/marketplace/available`).catch(() => ({ data: [] })),
          api.get(`/organizations/${user.orgId}/marketplace/consumer-of`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setMarketplaceCourses(Array.isArray(availResp.data) ? availResp.data : []);
        setMarketplaceConsumerOf(Array.isArray(consumerOfResp.data) ? consumerOfResp.data : []);
      } finally {
        if (!cancelled) setLoadingMarketplace(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lifecycleFilter, user?.orgId]);

  // PRD v2.1 / Slice S8 — Resolve source-org names for any imported courses
  // visible in the grid. Batched lookup against the existing helper endpoint.
  useEffect(() => {
    const orgIds = new Set();
    courses.forEach((c) => {
      const pOrg = c?.sourceTemplate?.parentOrgId;
      if (pOrg && !sourceOrgNames[pOrg]) orgIds.add(pOrg);
    });
    if (orgIds.size === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(`/organizations/_lookup?ids=${[...orgIds].join(',')}`);
        if (cancelled) return;
        const next = {};
        (resp.data || []).forEach((o) => { next[o.id] = { name: o.name, logoUrl: o.logoUrl }; });
        setSourceOrgNames((prev) => ({ ...prev, ...next }));
      } catch (_e) {
        /* silently fall back to raw id */
      }
    })();
    return () => { cancelled = true; };
  }, [courses, sourceOrgNames]);

  
  // Listen for org switch event to refetch data
  useEffect(() => {
    const handleOrgSwitch = () => {
      // Reset pagination and filters on org switch
      resetUrlFilters();
      clearAdvancedFilters();
      // Reset fetch guard so new org can fetch immediately
      setFetchingCourses(false);
      lastFetchedOrgRef.current = null;
    };
    
    window.addEventListener('org-switched', handleOrgSwitch);
    return () => window.removeEventListener('org-switched', handleOrgSwitch);
  }, []);
  
  const fetchCourses = async () => {
    // Skip if already fetching for the SAME org to prevent duplicate requests
    // Allow re-fetch if org has changed
    if (fetchingCourses && lastFetchedOrgRef.current === user?.orgId) return;
    
    lastFetchedOrgRef.current = user?.orgId;
    setFetchingCourses(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', coursesPerPage.toString());
      
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      if (sortBy) {
        params.append('sort', sortBy);
      }
      if (difficultyFilter && difficultyFilter !== 'all') {
        params.append('difficulty', difficultyFilter);
      }
      if (domainFilter && domainFilter !== 'all') {
        params.append('domain', domainFilter);
      }
      if (publishedFilter === 'published') {
        params.append('published_only', 'true');
      }
      // PRD: Course Merger -- lifecycle stage filter from the tab selection.
      // 'all' means we send no filter (server defaults to populated+published
      // for instructors so drafts/archived only surface when explicitly
      // requested via the tabs). 'marketplace' is a frontend-only pseudo-stage
      // (handled by the parallel marketplace useEffect) so we skip the param.
      if (lifecycleFilter && lifecycleFilter !== 'all' && lifecycleFilter !== 'marketplace') {
        params.append('lifecycleStage', lifecycleFilter);
      }
      if (taxonomyTagIds.length > 0) {
        params.append('tag_ids', taxonomyTagIds.join(','));
      }

      // PRD v2.2 / S2.6 — server-side kind filter. Forks are excluded by
      // default so the totalCourses count + pagination stay consistent.
      // The "Published" lifecycle tab opts in to include forks (they ARE
      // published courses linked to a cohort) and the optional
      // "show forks" toggle widens it back to everything.
      const includeForks = showForks || lifecycleFilter === 'published' || lifecycleFilter === 'all';
      if (!includeForks) {
        params.append('kind', 'template');
      }
      
      const response = await api.get(`/organizations/${user.orgId}/courses?${params.toString()}`);
      const data = response.data;
      
      // Handle both old array response and new paginated response
      if (Array.isArray(data)) {
        setCourses(data);
        setTotalCourses(data.length);
        setTotalPages(1);
      } else {
        setCourses(data.courses || []);
        setTotalCourses(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
      
      // Only fetch creator information if user is instructor/admin
      if (user?.role === 'Instructor' || user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin') {
        try {
          const usersResponse = await api.get(`/organizations/${user.orgId}/users/basic`);
          const usersMap = {};
          usersResponse.data.forEach(u => {
            usersMap[u.id] = u;
          });
          setCreators(usersMap);
        } catch (usersError) {
          // Silently fail - creator info is nice-to-have, not essential
          console.log('Could not fetch users:', usersError.message);
        }
      }
    } catch (error) {
      toast.error('Error', { description: error.message });
    } finally {
      setFetchingCourses(false);
    }
  };
  
  // Handle thumbnail upload for a course
  const handleThumbnailUpload = async (courseId, thumbnailUrl) => {
    try {
      await api.put(`/organizations/${user.orgId}/courses/${courseId}`, {
        thumbnailUrl: thumbnailUrl
      });
      // Update local state
      setCourses(prev => prev.map(c => 
        c.id === courseId ? { ...c, thumbnailUrl } : c
      ));
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(prev => ({ ...prev, thumbnailUrl }));
      }
    } catch (error) {
      throw error; // Let ImageCard handle the error
    }
  };
  
  // Fetch analytics for a specific course
  const fetchCourseAnalytics = async (courseId) => {
    try {
      setAnalyticsLoading(true);
      const response = await api.get(`/orgs/${user.orgId}/analytics/courses/${courseId}`);
      setCourseAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching course analytics:', error);
      // Set default empty analytics if fetch fails
      setCourseAnalytics({
        totalLearners: 0,
        avgProgress: 0,
        completionRate: 0,
        topicCompletionRates: [],
        engagementByWeek: [],
        dropOffPoints: []
      });
      toast.success('Analytics', { description: 'No analytics data available yet for this course.' });
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  const handleOpenAnalytics = (course) => {
    setShowAnalyticsDialog(true);
    fetchCourseAnalytics(course.id);
  };
  
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post(`/organizations/${user.orgId}/courses`, courseForm);
      toast.success('Success', { description: 'Course created successfully' });
      setIsDialogOpen(false);
      setCourseForm({ title: '', description: '', difficulty: 'Beginner', domain: '', duration: '' });
      
      // Navigate to course builder
      navigate(`/courses/${response.data.id}/edit`);
    } catch (error) {
      toast.error('Error', { description: error.message });
    } finally {
      setLoading(false);
    }
  };
  
  const handleImportCourse = async () => {
    try {
      // Create a file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        try {
          setLoading(true);
          
          // Read the file
          const fileContent = await file.text();
          
          // Parse JSON with error handling
          let courseData;
          try {
            courseData = JSON.parse(fileContent);
          } catch (parseError) {
            toast.error('Invalid JSON', { description: 'The file is not a valid JSON format. Please check the file and try again.' });
            setLoading(false);
            return;
          }
          
          // Determine the course title from various possible formats
          const courseTitle = courseData.title || 
                             courseData.courseData?.title || 
                             courseData.course?.title;
          
          // Validate required fields
          if (!courseTitle) {
            toast.error('Invalid Course Data', { description: 'Course file must have a "title" field.' });
            setLoading(false);
            return;
          }
          
          // Normalize the import data to always have { course: {...} } format
          let importData;
          
          if (courseData.courseData) {
            // Content Builder export format: { title, courseData: { topics: [...] } }
            importData = {
              course: {
                title: courseData.title || courseData.courseData.title,
                description: courseData.description || courseData.targetAudience || '',
                learningObjectives: courseData.learningObjectives || courseData.courseData.learningObjectives || [],
                targetAudience: courseData.targetAudience,
                prerequisites: courseData.prerequisites || [],
                duration: courseData.duration,
                difficulty: courseData.difficulty || 'Beginner',
                domain: courseData.domain,
                topics: (courseData.courseData.topics || []).map(topic => ({
                  name: topic.name || topic.title,
                  summary: topic.summary || '',
                  order: topic.order || 0,
                  pedagogy: topic.pedagogy || [],
                  contentBlocks: (topic.contentBlocks || topic.blocks || []).map(block => ({
                    type: block.type || 'RICHTEXT',
                    title: block.title || 'Content',
                    order: block.order || 0,
                    content: block.content ? JSON.parse(JSON.stringify(block.content)) : {},
                    settings: block.settings ? JSON.parse(JSON.stringify(block.settings)) : {}
                  }))
                }))
              }
            };
          } else if (courseData.course) {
            // Already in correct format: { course: { title, topics: [...] } }
            importData = courseData;
          } else {
            // Direct course format: { title, topics: [...] }
            importData = {
              course: {
                title: courseData.title,
                description: courseData.description || '',
                learningObjectives: courseData.learningObjectives || [],
                targetAudience: courseData.targetAudience || '',
                prerequisites: courseData.prerequisites || [],
                duration: courseData.duration || '',
                difficulty: courseData.difficulty || 'Beginner',
                domain: courseData.domain || '',
                topics: (courseData.topics || []).map(topic => ({
                  name: topic.name || topic.title,
                  summary: topic.summary || '',
                  order: topic.order || 0,
                  pedagogy: topic.pedagogy || [],
                  contentBlocks: (topic.contentBlocks || topic.blocks || []).map(block => ({
                    type: block.type || 'RICHTEXT',
                    title: block.title || 'Content',
                    order: block.order || 0,
                    content: block.content ? JSON.parse(JSON.stringify(block.content)) : {},
                    settings: block.settings ? JSON.parse(JSON.stringify(block.settings)) : {}
                  }))
                }))
              }
            };
          }
          
          console.log('Import data prepared:', importData);
          
          // Validate import data has topics
          if (!importData.course?.topics || importData.course.topics.length === 0) {
            toast.warning('Warning', { description: 'Course imported but contains no topics. You may want to add content.' });
          }
          
          // Send to backend
          const response = await api.post(`/organizations/${user.orgId}/courses/import`, importData);
          
          // Check response
          if (!response.data || !response.data.courseId) {
            throw new Error('Import succeeded but no course ID was returned');
          }
          
          toast.success('Success', { description: `Course "${courseData.title || courseData.courseData?.title || 'imported course'}" imported successfully!` });
          
          // Refresh courses list
          await fetchCourses();
          
          // Small delay before navigation to ensure state updates
          setTimeout(() => {
            navigate(`/courses/${response.data.courseId}/edit`);
          }, 500);
          
        } catch (error) {
          console.error('Error importing course:', error);
          
          // Detailed error message
          let errorMessage = 'Failed to import course';
          if (error.message) {
            errorMessage = error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast.error('Import Failed', { description: errorMessage });
        } finally {
          setLoading(false);
        }
      };
      
      // Trigger file selection
      input.click();
    } catch (error) {
      console.error('Error in import flow:', error);
      toast.error('Error', { description: 'Failed to open file selector. Please try again.' });
    }
  };
  
  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await api.delete(`/organizations/${user.orgId}/courses/${courseId}`);
      toast.success('Success', { description: 'Course deleted successfully' });
      fetchCourses();
    } catch (error) {
      toast.error('Error', { description: error.message });
    }
  };
  
  const getDifficultyColor = (difficulty) => {
    const colors = {
      Beginner: 'bg-green-100 text-green-800',
      Intermediate: 'bg-yellow-100 text-yellow-800',
      Advanced: 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };
  
  const getCreatorRoleColor = (role) => {
    return role === 'TenantAdmin' ? 'text-purple-600' : 'text-blue-600';
  };
  
  const isInstructor = user?.role === 'Instructor' || user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin';

  // Unsaved Course Studio drafts reopen their chat instead of the old wizard.
  useEffect(() => {
    if (!isInstructor || !user?.orgId) return;
    listDrafts(user.orgId)
      .then((r) => setStudioCourseIds(new Set((r.drafts || []).flatMap((d) => d.courseIds || [d.courseId]))))
      .catch(() => {});
  }, [isInstructor, user?.orgId]);


  // Client-side filtering for instructor and date filters (server handles search, difficulty, domain, published, taxonomy, kind)
  const filteredCourses = courses.filter(course => {
    // Multi-select instructor filter - course must have at least one selected instructor as creator or collaborator
    const matchesInstructors = selectedInstructors.length === 0 || 
      selectedInstructors.some(instrId => 
        course.createdBy === instrId || course.collaborators?.includes(instrId)
      );
    
    // Date range filter (client-side)
    let matchesDateRange = true;
    if (dateRange.start) {
      matchesDateRange = matchesDateRange && new Date(course.createdAt) >= new Date(dateRange.start);
    }
    if (dateRange.end) {
      matchesDateRange = matchesDateRange && new Date(course.createdAt) <= new Date(dateRange.end);
    }

    // Note: fork-vs-template filtering now happens server-side via the
    // `kind` query param (driven by `showForks` + lifecycle tab choice).
    // Doing it here would break the totalCourses/pagination math.
    return matchesInstructors && matchesDateRange;
  });
  
  // Get instructors only (no learners)
  const instructorsList = Object.values(creators).filter((c, i, self) => 
    (c.role === 'Instructor' || c.role === 'TenantAdmin') &&
    self.findIndex(t => t.id === c.id) === i
  );
  
  // Get unique domains for filter (from current page results)
  const uniqueDomains = [...new Set(courses.map(c => c.domain).filter(Boolean))];
  
  // Download Content Pack PDF
  const handleDownloadContentPack = async (courseId, courseTitle) => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('accessToken');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(
        `${BACKEND_URL}/api/organizations/${user.orgId}/courses/${courseId}/content-pack`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courseTitle?.replace(/[^a-z0-9]/gi, '_') || 'Course'}_Content_Pack.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Success', { description: 'Content Pack PDF downloaded successfully!' });
    } catch (error) {
      console.error('Error downloading content pack:', error);
      toast.error('Error', { description: 'Failed to download Content Pack PDF' });
    } finally {
      setDownloadingPdf(false);
    }
  };
  
  if (!user?.orgId) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Loading...</p>
        </div>
      </MainLayout>
    );
  }
  
  // Right panel content
  const rightPanelContent = selectedCourse ? (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{selectedCourse.title}</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge className={getDifficultyColor(selectedCourse.difficulty)}>
            {selectedCourse.difficulty}
          </Badge>
          {selectedCourse.isPublished ? (
            <Badge className="bg-green-100 text-green-800 whitespace-nowrap">✓ Published</Badge>
          ) : (
            <Badge className="bg-gray-100 text-gray-700 whitespace-nowrap">○ Not Published</Badge>
          )}
          {selectedCourse.sourceTemplate?.parentOrgId && (
            <Badge className="bg-sky-100 text-sky-800 border border-sky-200 whitespace-nowrap flex items-center gap-1">
              <Globe className="w-3 h-3" /> Imported
            </Badge>
          )}
        </div>

        {/* PRD v2.1 / Slice S8 — Marketplace origin block. Shows full
            provenance for cross-org imports so admins can trace what came
            from where and stay aware of source-version drift. */}
        {selectedCourse.sourceTemplate?.parentOrgId && (
          <div
            className="text-sm mb-4 bg-sky-50 rounded-lg p-3 border border-sky-200"
            data-testid="course-marketplace-origin-panel"
          >
            <div className="flex items-center gap-2 text-sky-800 font-medium mb-2">
              <Globe className="w-4 h-4" />
              <span>Marketplace origin</span>
            </div>
            <div className="space-y-1.5 text-xs text-sky-900">
              <div>
                <span className="text-sky-700">From: </span>
                <span className="font-medium" data-testid="course-marketplace-origin-org">
                  {sourceOrgNames[selectedCourse.sourceTemplate.parentOrgId]?.name
                    || selectedCourse.sourceTemplate.parentOrgId}
                </span>
              </div>
              {selectedCourse.sourceTemplate.parentCourseTitle && (
                <div>
                  <span className="text-sky-700">Original course: </span>
                  <span className="font-medium italic" data-testid="course-marketplace-origin-title">
                    &quot;{selectedCourse.sourceTemplate.parentCourseTitle}&quot;
                  </span>
                </div>
              )}
              {selectedCourse.sourceTemplate.parentCourseVersion && (
                <div>
                  <span className="text-sky-700">Source version: </span>
                  <span className="font-medium">v{selectedCourse.sourceTemplate.parentCourseVersion}</span>
                </div>
              )}
              {selectedCourse.sourceTemplate.importedAt && (
                <div>
                  <span className="text-sky-700">Imported: </span>
                  <span className="font-medium">
                    {(() => {
                      // Backend stores `importedAt` as UTC, but Motor strips
                      // the tz suffix on the way out (older rows look like
                      // `2026-06-01T12:49:04.743000`). Treat any bare ISO as
                      // UTC so `toLocaleString` renders the user's local time
                      // correctly. Strings that already carry `Z` or `±HH:MM`
                      // pass through untouched.
                      const raw = selectedCourse.sourceTemplate.importedAt;
                      const isUtcQualified = /Z$|[+-]\d{2}:?\d{2}$/.test(raw);
                      const normalized = isUtcQualified ? raw : `${raw}Z`;
                      return new Date(normalized).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                        timeZoneName: 'short',
                      });
                    })()}
                  </span>
                </div>
              )}
              {selectedCourse.sourceTemplate.importedByEmail && (
                <div>
                  <span className="text-sky-700">Imported by: </span>
                  <span className="font-medium">{selectedCourse.sourceTemplate.importedByEmail}</span>
                </div>
              )}
              <div className="pt-1 text-[11px] text-sky-700 italic">
                Edits made here stay isolated from the source course.
              </div>
            </div>
          </div>
        )}

        {/* Creator Information */}
        {creators[selectedCourse.createdBy] && (
          <div className="text-sm mb-2">
            <span className="text-gray-600">Created by: </span>
            <span className={`font-medium ${getCreatorRoleColor(creators[selectedCourse.createdBy].role)}`}>
              {creators[selectedCourse.createdBy].firstName} {creators[selectedCourse.createdBy].lastName}
            </span>
            <span className="text-xs text-gray-500 ml-2">
              ({creators[selectedCourse.createdBy].role === 'TenantAdmin' ? 'Admin' : 'Instructor'})
            </span>
          </div>
        )}
        
        {/* Collaborators Information */}
        {selectedCourse.collaborators && selectedCourse.collaborators.length > 0 && (
          <div className="text-sm mb-4 bg-purple-50 rounded-lg p-3 border border-purple-100">
            <div className="flex items-center gap-2 text-purple-700 font-medium mb-2">
              <UsersIcon className="w-4 h-4" />
              <span>Collaborators ({selectedCourse.collaborators.length})</span>
            </div>
            <div className="space-y-1">
              {selectedCourse.collaborators.map((collabId, idx) => {
                const collaborator = creators[collabId];
                return (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                    {collaborator ? (
                      <>
                        <span className="font-medium text-gray-800">
                          {collaborator.firstName} {collaborator.lastName}
                        </span>
                        {collabId === user?.id && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700">You</Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500 italic">Unknown user</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {selectedCourse.description && (
        <div className="group relative">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
          <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-5 cursor-pointer" data-testid="course-description-preview">
            {selectedCourse.description}
          </p>
          {selectedCourse.description.length > 300 && (
            <div className="hidden group-hover:block absolute left-0 top-0 z-50 w-full max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl p-4" data-testid="course-description-full">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
              <p className="text-sm text-gray-600 whitespace-pre-line">{selectedCourse.description}</p>
            </div>
          )}
        </div>
      )}
      
      <div className="space-y-3">
        {selectedCourse.domain && (
          <div className="flex items-center gap-2 text-sm">
            <BookOpen className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Domain:</span>
            <span className="font-medium">{selectedCourse.domain}</span>
          </div>
        )}
        {selectedCourse.duration && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Duration:</span>
            <span className="font-medium">{selectedCourse.duration}</span>
          </div>
        )}
        {selectedCourse.updatedAt && formatEditedLong(selectedCourse.updatedAt) && (
          <div className="flex items-center gap-2 text-sm" data-testid="course-last-edited">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Last edited:</span>
            <span className="font-medium">{formatEditedLong(selectedCourse.updatedAt)}</span>
          </div>
        )}
      </div>
      
      {selectedCourse.learningObjectives?.length > 0 && (
        <div className="group/lo relative">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Learning Objectives</h4>
          <ul className="space-y-1" data-testid="course-objectives-preview">
            {selectedCourse.learningObjectives.slice(0, 4).map((obj, idx) => {
              const cleanText = obj.replace(/^[\s]*(\d+[\.\)]\s*|[-•–—]\s*|\(\d+\)\s*)/, '').trim();
              return (
                <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="text-orange-500 font-medium mt-0.5 shrink-0 text-xs w-4 text-right">{idx + 1}.</span>
                  <span>{cleanText}</span>
                </li>
              );
            })}
            {selectedCourse.learningObjectives.length > 4 && (
              <li className="text-xs text-gray-400 cursor-pointer pl-6">+{selectedCourse.learningObjectives.length - 4} more (hover to see all)</li>
            )}
          </ul>
          {selectedCourse.learningObjectives.length > 4 && (
            <div className="hidden group-hover/lo:block absolute left-0 top-0 z-50 w-full max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl p-4" data-testid="course-objectives-full">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Learning Objectives</h4>
              <ul className="space-y-1">
                {selectedCourse.learningObjectives.map((obj, idx) => {
                  const cleanText = obj.replace(/^[\s]*(\d+[\.\)]\s*|[-•–—]\s*|\(\d+\)\s*)/, '').trim();
                  return (
                    <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-orange-500 font-medium mt-0.5 shrink-0 text-xs w-4 text-right">{idx + 1}.</span>
                      <span>{cleanText}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Thumbnail Editor - Only for instructors */}
      {isInstructor && (
        <ThumbnailEditor
          thumbnailUrl={selectedCourse.thumbnailUrl}
          onThumbnailChange={handleThumbnailUpload}
          orgId={user.orgId}
          itemId={selectedCourse.id}
          itemType="course"
        />
      )}
      
      <div className="pt-4 border-t space-y-2">
        {isInstructor ? (
          <>
            <Button
              className="w-full"
              onClick={() => navigate(`/courses/${selectedCourse.id}/edit`)}
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Course
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate(`/courses/${selectedCourse.id}`)}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Course
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleOpenAnalytics(selectedCourse)}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              variant="outline"
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0"
              onClick={() => handleDownloadContentPack(selectedCourse.id, selectedCourse.title)}
              disabled={downloadingPdf}
              data-testid="btn-download-content-pack"
            >
              {downloadingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Content Pack PDF
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="w-full text-red-600 hover:text-red-700"
              onClick={() => handleDeleteCourse(selectedCourse.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Course
            </Button>
          </>
        ) : (
          selectedCourse.isPublished && (
            <Button
              className="w-full bg-[#F97316] hover:bg-[#F59E0B]"
              onClick={() => navigate(`/courses/${selectedCourse.id}`)}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Course
            </Button>
          )
        )}
      </div>
    </div>
  ) : isInstructor ? (
    <CourseAnalyticsSummary />
  ) : null;
  
  return (
    <MainLayout rightPanel={rightPanelContent}>
      <div className="space-y-4 md:space-y-6">
        {/* Header - Aligned with Industry Labs style */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Course Management</h1>
              <p className="text-sm text-gray-500">{isInstructor ? 'Create, manage and publish your courses' : 'Browse available courses'}</p>
            </div>
          </div>
          
          {/* Action Buttons - Right side */}
          {isInstructor && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => {
                  // New course-package (zip) importer for all authors. The old
                  // JSON importer (`handleImportCourse`) stays in the code and
                  // its endpoint remains, but is no longer wired to this button.
                  setShowImportDialog(true);
                }}
                disabled={loading}
                data-testid="btn-import-course"
                size="sm"
                className="md:size-default"
              >
                <Upload className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Import Course</span>
              </Button>
              <Button
                onClick={() => navigate('/courses/new')}
                data-testid="btn-create-course"
                size="sm"
                className="md:size-default"
              >
                <Plus className="w-4 h-4 md:mr-2" />
                <span className="hidden sm:inline">Create Course</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/courses/wizard/new')}
                data-testid="btn-course-studio"
                size="sm"
                className="md:size-default"
              >
                <Sparkles className="w-4 h-4 md:mr-2" />
                <span className="hidden sm:inline">Course Studio</span>
              </Button>

              {/* Legacy quick-create dialog (kept for compatibility; will be
                  removed in Gate U5 alongside ContentBuilder cleanup). */}
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent data-testid="dialog-create-course">
                  <DialogHeader>
                    <DialogTitle>Create New Course</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCourse} className="space-y-4">
                    <div>
                      <Label htmlFor="title">Course Title</Label>
                      <Input
                        id="title"
                        value={courseForm.title}
                        onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
                        required
                        data-testid="input-course-title"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={courseForm.description}
                        onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                        data-testid="input-course-description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="difficulty">Difficulty</Label>
                        <Select
                          value={courseForm.difficulty}
                          onValueChange={(value) => setCourseForm({...courseForm, difficulty: value})}
                        >
                          <SelectTrigger data-testid="select-course-difficulty">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Beginner">Beginner</SelectItem>
                            <SelectItem value="Intermediate">Intermediate</SelectItem>
                            <SelectItem value="Advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="duration">Duration</Label>
                        <Input
                          id="duration"
                          placeholder="e.g., 4 Weeks"
                          value={courseForm.duration}
                          onChange={(e) => setCourseForm({...courseForm, duration: e.target.value})}
                          data-testid="input-course-duration"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="domain">Domain</Label>
                      <Input
                        id="domain"
                        placeholder="e.g., AI Fundamentals"
                        value={courseForm.domain}
                        onChange={(e) => setCourseForm({...courseForm, domain: e.target.value})}
                        data-testid="input-course-domain"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={loading} data-testid="btn-submit-course">
                        {loading ? 'Creating...' : 'Create & Edit'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
        
        {/* PRD: Course Merger -- Lifecycle stage tabs.
            All | Drafts | Frameworks | Populated | Published | Archived
            Hidden from Learners (they only ever see published). */}
        {isInstructor && (
          <div
            className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 pb-1 border-b border-gray-200"
            data-testid="course-lifecycle-tabs"
          >
            {[
              { value: 'all', label: 'All', dot: 'bg-gray-400' },
              { value: 'definition', label: 'Drafts', dot: 'bg-slate-400' },
              { value: 'framework', label: 'Frameworks', dot: 'bg-violet-500' },
              { value: 'populated', label: 'Populated', dot: 'bg-amber-500' },
              { value: 'published', label: 'Published', dot: 'bg-emerald-500' },
              { value: 'archived', label: 'Archived', dot: 'bg-zinc-400' },
              // PRD Cohort-First v2.1 / S7 — Marketplace pill toggles a separate view.
              { value: 'marketplace', label: 'Marketplace', dot: 'bg-sky-500' },
            ].map((t) => {
              const active = lifecycleFilter === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setLifecycleFilter(t.value)}
                  data-testid={`tab-lifecycle-${t.value}`}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    active
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="space-y-3">
              {/* Main Search Bar with Advanced Toggle */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full"
                    data-testid="input-search-courses"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[150px] sm:w-[180px] shrink-0" data-testid="courses-sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Last edited</SelectItem>
                    <SelectItem value="created_desc">Date created</SelectItem>
                    <SelectItem value="title_asc">Name (A–Z)</SelectItem>
                    <SelectItem value="title_desc">Name (Z–A)</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant={showAdvancedSearch || hasActiveAdvancedFilters ? "default" : "outline"}
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className={`shrink-0 ${hasActiveAdvancedFilters ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                  data-testid="btn-advanced-search"
                >
                  <SlidersHorizontal className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Advanced</span>
                  {hasActiveAdvancedFilters && (
                    <Badge className="ml-1 sm:ml-2 bg-white text-orange-600 text-xs px-1.5">
                      {[difficultyFilter !== 'all', domainFilter !== 'all', publishedFilter !== 'all', selectedInstructors.length > 0, dateRange.start, dateRange.end].filter(Boolean).length}
                    </Badge>
                  )}
                  {showAdvancedSearch ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                </Button>
              </div>
              
              {/* Advanced Filters - Collapsible */}
              {showAdvancedSearch && (
                <div className="border rounded-lg p-3 sm:p-4 bg-slate-50 space-y-4 animate-in slide-in-from-top-2 duration-200">
                  {/* Header with Clear All */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Advanced Filters</span>
                    {hasActiveAdvancedFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAdvancedFilters}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7 px-2"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear All
                      </Button>
                    )}
                  </div>
                  
                  {/* Row 1: Main filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">Difficulty</Label>
                      <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
                        <SelectTrigger data-testid="filter-difficulty" className="h-9">
                          <SelectValue placeholder="All Difficulties" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Difficulties</SelectItem>
                          <SelectItem value="Beginner">Beginner</SelectItem>
                          <SelectItem value="Intermediate">Intermediate</SelectItem>
                          <SelectItem value="Advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">Domain</Label>
                      <Select value={domainFilter} onValueChange={setDomainFilter}>
                        <SelectTrigger data-testid="filter-domain" className="h-9">
                          <SelectValue placeholder="All Domains" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Domains</SelectItem>
                          {uniqueDomains.map(domain => (
                            <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">Status</Label>
                      <Select value={publishedFilter} onValueChange={setPublishedFilter}>
                        <SelectTrigger data-testid="filter-status" className="h-9">
                          <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="unpublished">Unpublished</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer h-9">
                        <input
                          type="checkbox"
                          checked={showForks}
                          onChange={(e) => setShowForks(e.target.checked)}
                          className="rounded border-gray-300"
                          data-testid="filter-show-forks"
                        />
                        Include cohort forks
                      </label>
                    </div>
                  </div>
                  
                  {/* Row 2: Date Range */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">Created From</Label>
                      <Input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">Created To</Label>
                      <Input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Multi-select Instructors */}
                  {instructorsList.length > 0 && (
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">
                        Filter by Instructors/Admins
                      </Label>
                      
                      {/* Selected Instructors Display */}
                      {selectedInstructors.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                          {selectedInstructors.map(instrId => {
                            const instructor = instructorsList.find(i => i.id === instrId);
                            return instructor ? (
                              <span
                                key={instrId}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500 text-white rounded-full text-xs font-medium"
                              >
                                {instructor.firstName} {instructor.lastName}
                                {instructor.role === 'TenantAdmin' && ' (Admin)'}
                                <button
                                  onClick={() => setSelectedInstructors(prev => prev.filter(id => id !== instrId))}
                                  className="ml-0.5 hover:bg-orange-600 rounded-full p-0.5"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                      
                      {/* Instructor Selection Dropdown */}
                      <Select 
                        value="placeholder" 
                        onValueChange={(value) => {
                          if (value !== 'placeholder' && !selectedInstructors.includes(value)) {
                            setSelectedInstructors(prev => [...prev, value]);
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="+ Add Instructor/Admin to filter" />
                        </SelectTrigger>
                        <SelectContent>
                          {instructorsList.map(instructor => (
                            <SelectItem 
                              key={instructor.id} 
                              value={instructor.id}
                              disabled={selectedInstructors.includes(instructor.id)}
                            >
                              {instructor.firstName} {instructor.lastName}
                              {instructor.role === 'TenantAdmin' && ' (Admin)'}
                              {selectedInstructors.includes(instructor.id) && ' ✓'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Taxonomy Tag Filters */}
                  <TaxonomyFilterSidebar
                    orgId={user.orgId}
                    selectedTagIds={taxonomyTagIds}
                    onFilterChange={(ids) => { setTaxonomyTagIds(ids); setCurrentPage(1); }}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Course Count and Loading State */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            {fetchingCourses ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                Loading courses...
              </span>
            ) : (
              <span>
                {totalCourses > 0 ? (
                  <>Showing {filteredCourses.length} of {totalCourses} courses</>
                ) : (
                  <>No courses found</>
                )}
                {(debouncedSearch || hasActiveAdvancedFilters) && (
                  <span className="text-orange-600 ml-2">(filtered)</span>
                )}
              </span>
            )}
          </div>
        </div>
        
        {/* Courses Grid */}
        {lifecycleFilter === 'marketplace' ? (
          <div data-testid="marketplace-grid-wrapper">
            {loadingMarketplace ? (
              <div className="text-sm text-gray-500 py-8 text-center">Loading marketplace courses…</div>
            ) : marketplaceCourses.length === 0 ? (
              (() => {
                // PRD Cohort-First v2.1 / S7 polish — differentiated empty states.
                const summary = marketplaceConsumerOf || [];
                const totalListings = summary.reduce((acc, m) => acc + (m.listingCount || 0), 0);
                // (a) Not a consumer of any marketplace.
                if (summary.length === 0) {
                  return (
                    <div className="text-sm text-gray-600 py-10 px-6 text-center max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-lg" data-testid="marketplace-empty-no-marketplaces">
                      <div className="font-medium text-amber-900 mb-1">No marketplaces include your organisation yet</div>
                      <div className="text-amber-800">
                        Ask your SuperAdmin (in SuperAdmin Dashboard → Marketplaces → Configure) to add your org as a consumer of a marketplace. Then the source org's TenantAdmin can publish courses for you.
                      </div>
                    </div>
                  );
                }
                // (b) Consumer of N marketplaces but NONE have any published listings yet.
                if (totalListings === 0) {
                  return (
                    <div className="text-sm py-10 px-6 max-w-2xl mx-auto bg-sky-50 border border-sky-200 rounded-lg" data-testid="marketplace-empty-no-listings">
                      <div className="font-medium text-sky-900 mb-2 text-center">You're a consumer of {summary.length} marketplace{summary.length === 1 ? '' : 's'}, but no listings yet</div>
                      <div className="text-sky-800 text-center mb-3">
                        Ask the TenantAdmin of each source org below to publish at least one course listing (Course Builder → Edit Course → Marketplace tab):
                      </div>
                      <ul className="text-sky-900 space-y-1 list-disc list-inside text-center inline-block mx-auto">
                        {summary.map((m) => (
                          <li key={m.orgId} className="text-left">
                            <span className="font-medium">{m.name || m.orgId}</span>
                            <span className="text-xs text-sky-700 ml-1">({m.listingCount} listing{m.listingCount === 1 ? '' : 's'})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                // (c) Listings exist but Acme isn't on the allow-list for any of them.
                return (
                  <div className="text-sm py-10 px-6 max-w-2xl mx-auto bg-rose-50 border border-rose-200 rounded-lg" data-testid="marketplace-empty-not-allowed">
                    <div className="font-medium text-rose-900 mb-2 text-center">Listings exist, but your org isn't on the allow-list for any of them</div>
                    <div className="text-rose-800 text-center mb-3">
                      {summary.filter((m) => m.listingCount > 0).length} marketplace{summary.filter((m) => m.listingCount > 0).length === 1 ? ' has' : 's have'} published listings — but none currently include your org in their per-course allow-list. Reach out to the source-org TenantAdmin:
                    </div>
                    <ul className="text-rose-900 space-y-1 list-disc list-inside text-center inline-block mx-auto">
                      {summary.filter((m) => m.listingCount > 0).map((m) => (
                        <li key={m.orgId} className="text-left">
                          <span className="font-medium">{m.name || m.orgId}</span>
                          <span className="text-xs text-rose-700 ml-1">({m.listingCount} listing{m.listingCount === 1 ? '' : 's'} · 0 visible to you)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="marketplace-grid">
                {marketplaceCourses.map(({ course, sourceOrg }) => (
                  <div key={`${sourceOrg.id}-${course.id}`} className="relative border border-sky-200 rounded-lg overflow-hidden bg-white" data-testid={`marketplace-card-${course.id}`}>
                    {course.thumbnailUrl && (
                      <div className="h-40 bg-gray-100 overflow-hidden">
                        <img src={course.thumbnailUrl} alt={course.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-sky-100 text-sky-800 text-xs flex items-center gap-1">
                        <Globe className="w-3 h-3" /> From {sourceOrg.name}
                      </Badge>
                    </div>
                    <div className="p-4 space-y-2">
                      <h3 className="font-medium line-clamp-2">{course.title}</h3>
                      {course.description && <p className="text-xs text-gray-500 line-clamp-2">{course.description}</p>}
                      <div className="flex gap-2 flex-wrap">
                        {course.difficulty && <Badge variant="outline" className="text-xs">{course.difficulty}</Badge>}
                        {course.domain && <Badge variant="outline" className="text-xs">{course.domain}</Badge>}
                      </div>
                      <div className="pt-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setImportTarget({ course, sourceOrg })}
                          title="Import this course into your organization"
                          data-testid={`marketplace-import-btn-${course.id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1.5" />
                          Import
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="courses-grid">
          {filteredCourses.map((course) => {
            const creator = creators[course.createdBy];
            const creatorName = creator 
              ? `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || creator.email?.split('@')[0]
              : null;
            
            // PRD v2.2 / S2.6 — show a "Cohorts" deep-link badge on
            // templates that have been made available. Forks are
            // hidden from this list by default, so the badge effectively
            // appears on every template that's been published once.
            const showCohortsBadge = (course.kind === 'template' || !course.kind)
              && (course.isAvailableAsTemplate === true
                  || course.isPublished === true
                  || course.lifecycleStage === 'published');

            // 2026-02 — Draft-resume badge for `lifecycleStage: 'definition'`
            // cards. Uses the same step-validators the wizard's Next
            // button uses; label matches the step the user would land on
            // when they open the card. See `computeDraftResumeLabel`
            // helper at the top of this file.
            const draftResume = computeDraftResumeLabel(course);
            
            return (
              <div key={course.id} className="relative">
                <ImageCard
                  item={{
                    ...course,
                    creatorName: creatorName,
                    topicsCount: course.topicsCount || course.topics?.length
                  }}
                  type="course"
                  isSelected={selectedCourse?.id === course.id}
                  onClick={() => setSelectedCourse(course)}
                  onDoubleClick={() => {
                    if (user.role === 'Instructor' || user.role === 'TenantAdmin' || user.role === 'SuperAdmin') {
                      // PRD: Course Merger -- definition-stage rows resume in the wizard.
                      if (course.lifecycleStage === 'definition' && studioCourseIds.has(course.id)) {
                        navigate(`/courses/wizard/${course.id}`);
                      } else if (course.lifecycleStage === 'definition') {
                        navigate(`/courses/new/${course.id}`);
                      } else {
                        navigate(`/courses/${course.id}/edit`);
                      }
                    } else {
                      navigate(`/courses/${course.id}`);
                    }
                  }}
                  onImageUpload={handleThumbnailUpload}
                  showUploadButton={isInstructor}
                  orgId={user.orgId}
                  testId={`course-card-${course.id}`}
                />
                {/* PRD v2.1 / Slice S8 — Imported-from-marketplace badge. Top-left
                    to avoid collisions with the right-side Cohorts button + the
                    image upload affordance. Renders only for cross-org imports. */}
                {course.sourceTemplate?.parentOrgId && (
                  <div
                    className="absolute top-2 left-2 z-10 pointer-events-none"
                    data-testid={`imported-badge-${course.id}`}
                  >
                    <Badge className="bg-sky-100 text-sky-800 border border-sky-200 text-[10px] font-medium flex items-center gap-1 shadow-sm">
                      <Globe className="w-3 h-3" />
                      Imported
                      {sourceOrgNames[course.sourceTemplate.parentOrgId]?.name
                        ? ` · ${sourceOrgNames[course.sourceTemplate.parentOrgId].name}`
                        : ''}
                    </Badge>
                  </div>
                )}
                {showCohortsBadge && (user.role === 'Instructor' || user.role === 'TenantAdmin' || user.role === 'SuperAdmin') && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      // PRD Cohort-First v2.1 / S1 — smart routing:
                      //   1 cohort  → jump directly to /users/cohorts/<cohortId>
                      //   0 or 2+   → land on /users?tab=cohorts&courseId=<id> (filtered list)
                      try {
                        const { data } = await api.get(`/organizations/${user.orgId}/courses/${course.id}/cohorts`);
                        const ids = Array.isArray(data?.cohortIds) ? data.cohortIds : [];
                        if (ids.length === 1) {
                          navigate(`/users/cohorts/${ids[0]}`);
                          return;
                        }
                      } catch (_) {
                        // fall through to the filter list
                      }
                      navigate(`/users?tab=cohorts&courseId=${course.id}`);
                    }}
                    className="absolute top-2 right-2 z-10 bg-white/90 backdrop-blur-sm text-xs font-medium px-2 py-1 rounded-full shadow-sm hover:bg-white border border-gray-200 text-gray-700 hover:text-blue-600"
                    data-testid={`btn-course-cohorts-${course.id}`}
                    title="View cohorts running this course"
                  >
                    Cohorts &rarr;
                  </button>
                )}

                {/* 2026-02 — Draft-resume badge. Bottom-left corner
                    (above thumbnail, doesn't collide with Cohorts
                    button top-right or Imported badge top-left).
                    Amber when partial, emerald when ready-to-generate.
                    Clicking opens the draft at exactly its resume
                    step because the wizard's auto-jump uses the same
                    validators. */}
                {draftResume && (
                  <div
                    className="absolute bottom-2 left-2 z-10 pointer-events-none"
                    data-testid={`draft-resume-badge-${course.id}`}
                  >
                    <Badge
                      className={
                        draftResume.label === 'Ready to generate'
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-medium flex items-center gap-1 shadow-sm'
                          : 'bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-medium flex items-center gap-1 shadow-sm'
                      }
                    >
                      <Clock className="w-3 h-3" />
                      {draftResume.label}
                    </Badge>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
        
        {/* Pagination Controls */}
        {totalCourses > 0 && lifecycleFilter !== 'marketplace' && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-4 border-t">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="text-sm text-gray-600">
                {totalPages > 1 ? `Showing ${filteredCourses.length} of ${totalCourses} courses (Page ${currentPage} of ${totalPages})` : `${totalCourses} courses`}
              </div>
              <PageSizeSelector value={coursesPerPage} onChange={setCoursesPerPage} options={[12, 24, 48]} label="courses" />
            </div>
            {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || fetchingCourses}
                className="hidden sm:inline-flex"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || fetchingCourses}
              >
                <span className="sm:hidden">‹</span>
                <span className="hidden sm:inline">Previous</span>
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      disabled={fetchingCourses}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || fetchingCourses}
              >
                <span className="sm:hidden">›</span>
                <span className="hidden sm:inline">Next</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || fetchingCourses}
                className="hidden sm:inline-flex"
              >
                Last
              </Button>
            </div>
            )}
          </div>
        )}
        
        {filteredCourses.length === 0 && courses.length > 0 && (
          <div className="text-center py-16">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
          </div>
        )}
        
        {courses.length === 0 && (
          <div className="text-center py-16">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No courses yet</h3>
            <p className="text-gray-600 mb-4">
              {isInstructor ? 'Create your first course to get started' : 'No courses available at the moment'}
            </p>
          </div>
        )}
      </div>
      
      {/* Course Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onOpenChange={setShowAnalyticsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-500" />
              Course Analytics: {selectedCourse?.title}
            </DialogTitle>
          </DialogHeader>
          
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading analytics...</p>
              </div>
            </div>
          ) : courseAnalytics ? (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <UsersIcon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-700">{courseAnalytics.totalLearners || 0}</p>
                    <p className="text-xs text-blue-600">Total Learners</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-700">{Math.round(courseAnalytics.avgProgress || 0)}%</p>
                    <p className="text-xs text-green-600">Avg Progress</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-purple-700">{Math.round(courseAnalytics.completionRate || 0)}%</p>
                    <p className="text-xs text-purple-600">Completion Rate</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Topic Completion Rates */}
              {courseAnalytics.topicCompletionRates?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Topic Completion Rates</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {courseAnalytics.topicCompletionRates.map((topic, idx) => (
                      <div key={topic.topicId || idx}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-700 truncate mr-2">{topic.topicTitle}</span>
                          <span className="font-medium text-gray-900">{Math.round(topic.completionRate)}%</span>
                        </div>
                        <Progress value={topic.completionRate} className="h-2" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              
              {/* Drop-off Points */}
              {courseAnalytics.dropOffPoints?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-4 h-4" />
                      Drop-off Points
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {courseAnalytics.dropOffPoints.map((point, idx) => (
                        <li key={idx} className="text-sm text-amber-800 flex items-center gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                          <span>{point.topicTitle}</span>
                          <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs">
                            -{Math.round(point.dropRate)}%
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              
              {/* Weekly Engagement */}
              {courseAnalytics.engagementByWeek?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Weekly Engagement (Last 8 Weeks)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-1 h-24">
                      {courseAnalytics.engagementByWeek.map((count, idx) => {
                        const maxCount = Math.max(...courseAnalytics.engagementByWeek, 1);
                        const height = (count / maxCount) * 100;
                        return (
                          <div 
                            key={idx} 
                            className="flex-1 bg-orange-200 hover:bg-orange-300 rounded-t transition-colors"
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`Week ${idx + 1}: ${count} activities`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>8 weeks ago</span>
                      <span>This week</span>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* No Data Message */}
              {courseAnalytics.totalLearners === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No learner data available yet.</p>
                  <p className="text-xs mt-1">Analytics will appear once learners enroll and start the course.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Unable to load analytics data.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PRD v2.1 / Slice S8 — Cross-org marketplace import dialog. */}
      {importTarget && (
        <ImportMarketplaceCourseDialog
          open={!!importTarget}
          onOpenChange={(o) => { if (!o) setImportTarget(null); }}
          consumerOrgId={user?.orgId}
          sourceOrg={importTarget.sourceOrg}
          course={importTarget.course}
          onImported={(newCourseId) => {
            toast.success('Course imported', { description: `"${importTarget.course?.title}" is now in your Populated tab.` });
            if (newCourseId) {
              navigate(`/courses/${newCourseId}/edit`);
            }
          }}
        />
      )}

      {/* Course Package Import (zip) — Step 1 dialog, opens from btn-import-course. */}
      <ImportPackageDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        orgId={user?.orgId}
        orgName={orgName}
      />
    </MainLayout>
  );
};

export default CoursesPage;
